import React from "react";
import { STAGES } from "../constants/config";
import { SURFACE, BORDER, TEXT, ACCENT } from "../constants/colors";
import KanbanCard from "./KanbanCard";
import { formatCurrency } from "../utils/formatCurrency";

const KanbanColumn = ({
  stage,
  stageCards = [],
  dragOver,
  newCardIds = new Set(),
  removingCardIds = new Set(),
  movingCardIds = new Set(),
  onDragOver,
  onDragLeave,
  onDrop,
  onDragStart,
  onAddEvent,
}) => {
  return (
    <div
      style={{
        flexShrink: 0,
        width: 240,
        display: "flex",
        flexDirection: "column",
        maxHeight: "calc(100vh - 200px)",
      }}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver(stage.id);
      }}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, stage.id)}
    >
      {/* Column Header */}
      <div
        style={{
          padding: "10px 12px",
          background:
            dragOver === stage.id ? `${stage.color}15` : SURFACE.columnHeaderBg,
          borderRadius: "8px 8px 0 0",
          border: `1px solid ${dragOver === stage.id ? stage.color + "40" : BORDER.subtle}`,
          borderBottom: `3px solid ${stage.color}`,
          transition: "all 0.15s",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 3,
              }}
            >
              <span
                style={{
                  fontSize: 9,
                  fontFamily: "Poppins",
                  color: stage.color,
                  letterSpacing: 1,
                  fontWeight: 700,
                }}
              >
                {stage.short}
              </span>
              <span
                style={{ fontSize: 12, fontWeight: 700, color: TEXT.primary }}
              >
                {stage.label}
              </span>
              {/* Inline badge for Requisitions – same row, no extra height */}
              {stage.id === "backlog" && (
                <span
                  style={{
                    fontSize: 8,
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    color: stage.color,
                    background: `${stage.color}18`,
                    border: `1px solid ${stage.color}45`,
                    borderRadius: 3,
                    padding: "1px 5px",
                    letterSpacing: 0.2,
                    whiteSpace: "nowrap",
                  }}
                >
                  Next 30 days
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: 10,
                color: ACCENT.green,
                fontFamily: "Poppins",
              }}
            >
              {formatCurrency(
                stageCards.reduce((s, c) => s + (c.total_value || 0), 0),
              )}
            </div>
          </div>
        </div>

        {/* WIP Bar */}
        {stage.wip && (
          <div
            style={{
              marginTop: 6,
              height: 2,
              background: SURFACE.wipBarBg,
              borderRadius: 1,
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${Math.min(100, (stageCards.length / stage.wip) * 100)}%`,
                background: stage.color,
                borderRadius: 1,
                transition: "width 0.3s",
              }}
            />
          </div>
        )}
      </div>

      {/* Drop zone indicator */}
      {dragOver === stage.id && (
        <div style={{ height: 3, background: stage.color, borderRadius: 0 }} />
      )}

      {/* Cards */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "8px 0",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          background:
            dragOver === stage.id ? `${stage.color}05` : "transparent",
          border:
            dragOver === stage.id
              ? `1px dashed ${stage.color}30`
              : "1px solid transparent",
          borderRadius: "0 0 8px 8px",
          transition: "all 0.15s",
          paddingRight: 2,
        }}
      >
        {stageCards.length === 0 && (
          <div
            style={{
              padding: "20px 12px",
              textAlign: "center",
              color: BORDER.subtle,
              fontSize: 11,
              fontFamily: "Poppins",
            }}
          >
            {dragOver === stage.id ? "Drop here →" : "Empty"}
          </div>
        )}
        {stageCards.map((card, index) => (
          <KanbanCard
            key={card._id + index}
            card={card}
            onDragStart={onDragStart}
            stage={stage}
            isNew={newCardIds.has(card._id)}
            isRemoving={removingCardIds.has(card._id)}
            isMoving={movingCardIds.has(card._id)}
            onAddEvent={onAddEvent}
          />
        ))}
      </div>
    </div>
  );
};

export default KanbanColumn;
